import { useState } from 'react'
import { List } from '@chakra-ui/react'
import type { Customer } from '@leid/api-customers'
import { DistanceCoordinate, Region } from '@leid/api-locations'
import { sortByDistance } from 'sort-by-distance'

import { CustomerInfo } from 'app/utils/authentication'
import { SortingInfo } from 'appTypes/mapper'

import { RegionWithAvailability } from '..'

import { CustomerSelectedLocation } from './RegionsSectionHeader/customerSelectedLocation'
import RegionButton from './RegionButton'
import RegionsSectionHeader from './RegionsSectionHeader'

type Props = {
  title: string
  regions: RegionWithAvailability[]
  selectedRegionId: number | undefined
  onRegionChange: (selectedRegion: Region | undefined) => void

  customerInfo: CustomerInfo
  sortingInfo: SortingInfo | undefined
}

const RegionsList = (props: Props): JSX.Element => {
  const { title, regions, selectedRegionId, onRegionChange, customerInfo, sortingInfo } = props

  const [listSize] = useState(10)
  const [collapsed, setCollapsed] = useState(false)
  const [customerLocation, setCustomerLocation] = useState<CustomerSelectedLocation | undefined>(undefined)

  const getDistanceCoordinates = (
    region: Region | undefined,
    customerLocation: CustomerSelectedLocation | undefined,
    customer: Customer | null,
    sortingInfo: SortingInfo | undefined,
  ): DistanceCoordinate | undefined => {
    if (region) {
      return {
        latitudeCoordinate: region.lat,
        longitudeCoordinate: region.long,
      }
    }

    if (customerLocation) {
      return {
        latitudeCoordinate: customerLocation.latitude,
        longitudeCoordinate: customerLocation.longitude,
      }
    }

    if (sortingInfo) {
      return {
        latitudeCoordinate: sortingInfo.lat,
        longitudeCoordinate: sortingInfo.long,
      }
    }

    if (customer) {
      return {
        latitudeCoordinate: customer.coordinates?.latitude,
        longitudeCoordinate: customer.coordinates?.longitude,
      }
    }

    return undefined
  }

  const handleChange = (region: Region) => {
    if (selectedRegionId === region.id) {
      return
    }

    onRegionChange(region)
  }

  const handleCustomerSelectedLocationChange = (selectedLocation: CustomerSelectedLocation) => {
    if (selectedRegionId) {
      onRegionChange(undefined)
    }
    setCustomerLocation(selectedLocation)
  }

  const getRegionsToShow = () => {
    let region: Region | undefined
    if (selectedRegionId) {
      region = regions.find((s) => s.id === selectedRegionId)
    }

    const distanceCoordinates = getDistanceCoordinates(region, customerLocation, customerInfo.customer, sortingInfo)

    const sortedRegions = sortByDistance(
      { long: distanceCoordinates?.longitudeCoordinate, lat: distanceCoordinates?.latitudeCoordinate },
      regions,
      { yName: 'lat', xName: 'long' },
    ) as RegionWithAvailability[]

    // already sorted, so here we group them by availability
    const availableRegions = sortedRegions.filter((s) => s.hasAvailableLocations === true)
    const notAvailableRegions = sortedRegions.filter((s) => s.hasAvailableLocations === false)
    const emptyRegions = sortedRegions.filter((s) => s.hasAvailableLocations === undefined)

    const sortedList = [...availableRegions, ...notAvailableRegions, ...emptyRegions]

    if (selectedRegionId) {
      const selectedRegion = sortedList.find((s) => s.id === selectedRegionId)
      const newRegions = sortedList.filter((s) => s.id !== selectedRegionId)
      if (selectedRegion) {
        newRegions.unshift(selectedRegion)
      }
      return newRegions
    } else {
      return sortedList
    }
  }

  const regionsToShow = getRegionsToShow()

  return (
    <>
      <RegionsSectionHeader
        title={title}
        selectedRegionId={selectedRegionId}
        onCustomerLocationSelected={handleCustomerSelectedLocationChange}
        customerInfo={customerInfo}
      />

      <List.Root width="100%" marginBottom={5} marginTop={5} display={'flex'} flexDirection={'row'} flexWrap={'wrap'}>
        {regionsToShow.map((region, idx) => (
          <RegionButton
            key={region.id}
            index={idx}
            region={region}
            handleChange={handleChange}
            listSize={listSize}
            regionsLength={regions.length}
            collapsed={collapsed}
            setCollapsed={setCollapsed}
            selectedRegionId={selectedRegionId}
          />
        ))}
      </List.Root>
    </>
  )
}

export default RegionsList
